import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AddAPhoto as AddAPhotoIcon,
  CheckCircleRounded as ActiveIcon,
  EcoRounded as EcoIcon,
  EmojiNatureRounded as WildIcon,
  FlagRounded as FrenchIcon,
  RoomRounded as LocalIcon,
  SpaRounded as OrganicIcon,
  StarRounded as TopIcon,
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import { useCreateProduct } from '../../hooks/create-product.hook';
import { useUploadProductImage } from '../../hooks/upload-product-image.hook';
import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { useAdminGetTaxes } from '../../../taxes/hooks/admin-get-taxes.hook';
import { useAdminGetTags } from '../../../tags/hooks/admin-get-tags.hook';
import { useAdminGetWeightUnities } from '../../../weight-unities/hooks/admin-get-weight-unities.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { Tax } from '../../../taxes/types/tax.type';
import { Tag } from '../../../tags/types/tag.type';
import { WeightUnity } from '../../../weight-unities/types/weight-unity.type';
import { PRODUCT_SIZES, ProductSize } from '../../types/product-sizes.type';
import { CreateProductRequestDTO } from '../../types/dtos/create-product.dto';
import RichTextEditor from '../../../shared/components/Forms/RichTextEditor.component';

interface CreateProductDialogProps {
  open: boolean;
  onClose: () => void;
  onProductCreated: () => void;
}

interface CreateProductFormValues {
  name: string;
  reference: string;
  description: string;
  price: string;
  origin: string;
  stock: string;
  category: Category | null;
  subCategory: SubCategory | null;
  tax: Tax | null;
  weightQuantity: string;
  weightUnity: WeightUnity | null;
  size: ProductSize;
  tags: Tag[];
}

const FORM_DEFAULT_VALUES: CreateProductFormValues = {
  name: '',
  reference: '',
  description: '',
  price: '10',
  origin: '',
  stock: '100',
  category: null,
  subCategory: null,
  tax: null,
  weightQuantity: '',
  weightUnity: null,
  size: PRODUCT_SIZES.M,
  tags: [],
};

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    paddingBottom: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  topSection: {
    display: 'flex',
    gap: '1rem',
  },
  imagePlaceholder: {
    minWidth: 190,
    width: 190,
    height: 190,
    border: '2px dashed #ccc',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageIcon: {
    fontSize: 40,
    color: '#bbb',
  },
  fieldsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flex: 1,
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  chip: {
    cursor: 'pointer',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,

    '& .MuiChip-icon': {
      fontSize: 14,
      marginLeft: 6,
      marginRight: -4,
    },
  },
  chipSelected: {
    border: '1px solid transparent',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,

    '& .MuiChip-icon': {
      color: theme.palette.admin.primary.contrastText,
    },

    '&:hover, &:focus': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  chipDivider: {
    width: 1,
    height: 20,
    backgroundColor: '#ccc',
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
  },
  actions: {
    padding: '8px 24px 24px 24px',

    '& .MuiButton-root': {
      textTransform: 'none',
    },
  },
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

function CreateProductDialog({ open, onClose, onProductCreated }: CreateProductDialogProps) {
  const classes = useStyles();
  const { createProduct, isSubmitting: isCreating } = useCreateProduct();
  const { uploadProductImage, isSubmitting: isUploading } = useUploadProductImage();
  const { categories } = useAdminGetCategories();
  const { taxes } = useAdminGetTaxes();
  const { tags } = useAdminGetTags();
  const { weightUnities } = useAdminGetWeightUnities();

  const { control, handleSubmit, watch, setValue, reset } = useForm<CreateProductFormValues>({
    defaultValues: FORM_DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const selectedCategory = watch('category');
  const { subCategories } = useAdminGetSubCategories({ categoryId: selectedCategory?.id });

  const [organic, setOrganic] = useState(false);
  const [wild, setWild] = useState(false);
  const [french, setFrench] = useState(false);
  const [native, setNative] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isTop, setIsTop] = useState(false);
  const [isEco, setIsEco] = useState(false);

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setSelectedFile(file);
    }
  };

  const resetForm = () => {
    reset(FORM_DEFAULT_VALUES);
    setOrganic(false);
    setWild(false);
    setFrench(false);
    setNative(false);
    setIsActive(true);
    setIsTop(false);
    setIsEco(false);
    setImagePreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (formValues: CreateProductFormValues) => {
    if (!formValues.category || !formValues.subCategory || !formValues.tax || !formValues.weightUnity) return;

    const payload: CreateProductRequestDTO = {
      name: formValues.name,
      reference: formValues.reference,
      description: formValues.description,
      price: Number(formValues.price),
      subCategory: formValues.subCategory.id,
      taxId: formValues.tax.id,
      weight: { quantity: Number(formValues.weightQuantity), unityId: formValues.weightUnity.id },
      size: formValues.size,
      stock: Number(formValues.stock),
      labels: { organic, wild, french, native },
      isActive,
      isTop,
      isEco,
    };

    if (formValues.origin) payload.origin = formValues.origin;
    if (formValues.tags.length > 0) payload.tags = formValues.tags.map((tag) => tag.id);

    const createdProduct = await createProduct(payload);
    if (!createdProduct) return;

    if (selectedFile) await uploadProductImage(createdProduct.id, selectedFile);

    onProductCreated();
    onClose();
    resetForm();
  };

  const isSubmitting = isCreating || isUploading;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className={classes.title}>Nouveau produit</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <div className={classes.chipRow}>
            <Chip
              className={clsx(classes.chip, isActive && classes.chipSelected)}
              label="Actif"
              icon={<ActiveIcon />}
              size="small"
              variant={isActive ? 'default' : 'outlined'}
              onClick={() => setIsActive((previous) => !previous)}
            />

            <div className={classes.chipDivider} />

            <Chip
              className={clsx(classes.chip, organic && classes.chipSelected)}
              label="Bio"
              icon={<OrganicIcon />}
              size="small"
              variant={organic ? 'default' : 'outlined'}
              onClick={() => setOrganic((previous) => !previous)}
            />
            <Chip
              className={clsx(classes.chip, wild && classes.chipSelected)}
              label="Sauvage"
              icon={<WildIcon />}
              size="small"
              variant={wild ? 'default' : 'outlined'}
              onClick={() => setWild((previous) => !previous)}
            />
            <Chip
              className={clsx(classes.chip, french && classes.chipSelected)}
              label="Français"
              icon={<FrenchIcon />}
              size="small"
              variant={french ? 'default' : 'outlined'}
              onClick={() => setFrench((previous) => !previous)}
            />
            <Chip
              className={clsx(classes.chip, native && classes.chipSelected)}
              label="Local"
              icon={<LocalIcon />}
              size="small"
              variant={native ? 'default' : 'outlined'}
              onClick={() => setNative((previous) => !previous)}
            />

            <div className={classes.chipDivider} />

            <Chip
              className={clsx(classes.chip, isTop && classes.chipSelected)}
              label="Top"
              icon={<TopIcon />}
              size="small"
              variant={isTop ? 'default' : 'outlined'}
              onClick={() => setIsTop((previous) => !previous)}
            />
            <Chip
              className={clsx(classes.chip, isEco && classes.chipSelected)}
              label="Éco"
              icon={<EcoIcon />}
              size="small"
              variant={isEco ? 'default' : 'outlined'}
              onClick={() => setIsEco((previous) => !previous)}
            />
          </div>

          <div className={classes.topSection}>
            <div className={classes.imagePlaceholder} onClick={() => !isUploading && fileInputRef.current?.click()}>
              {isUploading ? (
                <CircularProgress size={40} />
              ) : imagePreview ? (
                <img src={imagePreview} alt="Aperçu" className={classes.imagePreview} />
              ) : (
                <AddAPhotoIcon className={classes.imageIcon} />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              hidden
              onChange={handleImageChange}
            />

            <div className={classes.fieldsColumn}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'Le nom est requis',
                  validate: (value) => value.trim().length > 0 || 'Le nom est requis',
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    required
                    label="Nom"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    {...field}
                  />
                )}
              />

              <div className={classes.row}>
                <Controller
                  name="reference"
                  control={control}
                  rules={{
                    required: 'La référence est requise',
                    validate: (value) => value.trim().length >= 2 || 'La référence doit contenir au moins 2 caractères',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      required
                      label="Référence"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="origin"
                  control={control}
                  render={({ field }) => (
                    <TextField size="small" variant="outlined" fullWidth label="Origine" {...field} />
                  )}
                />
              </div>

              <Controller
                name="description"
                control={control}
                rules={{ required: 'La description est requise' }}
                render={({ field, fieldState }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Description du produit"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <Controller
              name="category"
              control={control}
              rules={{ required: 'La rubrique est requise' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  fullWidth
                  size="small"
                  value={field.value}
                  options={[...categories].sort((first, second) => first.name.localeCompare(second.name))}
                  groupBy={(option) => option.name[0].toUpperCase()}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: Category | null) => {
                    field.onChange(selectedOption);
                    setValue('subCategory', null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="Rubrique"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="subCategory"
              control={control}
              rules={{ required: 'La sous-rubrique est requise' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  fullWidth
                  size="small"
                  value={field.value}
                  disabled={!selectedCategory}
                  options={[...subCategories].sort((first, second) => first.name.localeCompare(second.name))}
                  groupBy={(option) => option.name[0].toUpperCase()}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: SubCategory | null) => field.onChange(selectedOption)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="Sous-rubrique"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </div>

          <div className={classes.row}>
            <Controller
              name="price"
              control={control}
              rules={{
                required: 'Le prix est requis',
                validate: (value) => Number(value) > 0 || 'Le prix doit être supérieur à 0',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  label="Prix"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                  {...field}
                />
              )}
            />
            <Controller
              name="tax"
              control={control}
              rules={{ required: 'La TVA est requise' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  fullWidth
                  size="small"
                  value={field.value}
                  options={[...taxes].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: Tax | null) => field.onChange(selectedOption)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="TVA"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Taille</InputLabel>
                  <Select
                    native
                    label="Taille"
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value as ProductSize)}
                  >
                    {Object.values(PRODUCT_SIZES).map((productSize) => (
                      <option key={productSize} value={productSize}>
                        {productSize}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>

          <div className={classes.row}>
            <Controller
              name="weightQuantity"
              control={control}
              rules={{
                required: 'Le poids est requis',
                validate: (value) => Number(value) > 0 || 'Le poids doit être supérieur à 0',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  label="Poids (quantité)"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="weightUnity"
              control={control}
              rules={{ required: "L'unité de poids est requise" }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  fullWidth
                  size="small"
                  value={field.value}
                  options={[...weightUnities].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => `${option.name} (${option.abbreviation})`}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: WeightUnity | null) => field.onChange(selectedOption)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="Unité de poids"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </div>

          <div className={classes.row}>
            <Controller
              name="stock"
              control={control}
              rules={{ validate: (value) => Number(value) >= 0 || 'Le stock ne peut pas être négatif' }}
              render={({ field, fieldState }) => (
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Stock"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  value={field.value}
                  options={[...tags].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOptions: Tag[]) => field.onChange(selectedOptions)}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Tags" />}
                />
              )}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button size="small" variant="text" onClick={onClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          onClick={handleSubmit(onSubmit)}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateProductDialog;
